import { AxiosInstance } from 'axios';

import {
  IBookmarkContent,
  INewsContentBookmark,
  IPodcastContentBookmark,
  ITermContentBookmark,
  IVideoContentBookmark,
} from '@mais-retorno-front/models/content-bookmark.model';
import { ICatchIssueUseCase } from '../catch-issue.usecase';
import { IContent } from '@mais-retorno-front/models/content';
import { DEFAULT_ROUTES } from '@mais-retorno-front/constants/routes';
import { getVideoThumbFromYoutube } from '@mais-retorno-front/helpers/video-thumb';

interface IHandleResponse {
  posts: IContent[];
  podcasts: IContent[];
  videos: IContent[];
  terms: IContent[];
}

type GetAllBookmarkContentDetailsFormattedUseCaseProps = {
  handle: () => Promise<IHandleResponse>;
};

export class GetAllBookmarkContentDetailsFormattedUseCase
  implements GetAllBookmarkContentDetailsFormattedUseCaseProps
{
  constructor(
    private readonly url: string,
    private readonly http: AxiosInstance,
    private readonly getPostDatailsBySlug: (
      slugs: string[]
    ) => Promise<INewsContentBookmark[]>,
    private readonly getPodcastDatailsBySlug: (
      slugs: string[]
    ) => Promise<IPodcastContentBookmark[]>,
    private readonly getVideoDatailsBySlug: (
      slugs: string[]
    ) => Promise<IVideoContentBookmark[]>,
    private readonly getTermDatailsBySlug: (
      slugs: string[]
    ) => Promise<ITermContentBookmark[]>,
    private readonly catchIssueUseCase: ICatchIssueUseCase
  ) {}

  async handle(): Promise<IHandleResponse> {
    try {
      const { data } = await this.http.get<IBookmarkContent[]>(this.url);

      const postList = data.filter(
        (content) => content?.content_type === 'post'
      );
      const videoList = data.filter(
        (content) => content?.content_type === 'video'
      );
      const podcastList = data.filter(
        (content) => content?.content_type === 'podcast'
      );
      const termList = data.filter(
        (content) => content?.content_type === 'term'
      );

      const postSlugs = postList?.map((eachPost) =>
        JSON.stringify(eachPost?.content_slug)
      );
      const podcastSlugs = podcastList?.map((eachPodcast) =>
        JSON.stringify(eachPodcast?.content_slug)
      );

      const videoSlugs = videoList?.map((eachVideo) =>
        JSON.stringify(eachVideo?.content_slug)
      );
      const termSlugs = termList?.map((eachTerm) =>
        JSON.stringify(eachTerm?.content_slug)
      );

      const [
        allPostsReturned,
        allPodcastsReturned,
        allVideosReturned,
        allTermsReturned,
      ] = await Promise.all([
        this.getPostDatailsBySlug(postSlugs),
        this.getPodcastDatailsBySlug(podcastSlugs),
        this.getVideoDatailsBySlug(videoSlugs),
        this.getTermDatailsBySlug(termSlugs),
      ]);

      const postsFormatted: IContent[] = allPostsReturned?.map((post) => ({
        title: post?.attributes?.title,
        slug: post?.attributes?.slug,
        excerpt: post?.attributes?.excerpt,
        link: `${DEFAULT_ROUTES.portal}${post?.attributes?.slug}`,
        contentId: Number(post?.id),
        date: post?.attributes?.publishedAt,
        chapeu:
          post?.attributes?.chapeu ||
          post?.attributes?.categories?.data?.[0]?.attributes?.name,
        type: 'post',
        imageUrl:
          post?.attributes?.thumbnail?.data?.attributes?.url ||
          '/img/placeholder.png',
        imageTitle:
          post?.attributes?.thumbnail?.data?.attributes?.name ||
          post?.attributes?.thumbnail?.data?.attributes?.alternativeText,
        imageAltText:
          post?.attributes?.thumbnail?.data?.attributes?.alternativeText ||
          post?.attributes?.thumbnail?.data?.attributes?.name,
        seo: null,
        tags: {
          nodes: [],
        },
        categories: {
          nodes: post?.attributes?.categories?.data?.map((category) => {
            return {
              name: category?.attributes?.name,
            };
          }),
        },
      }));

      const podcastsFormatted: IContent[] = allPodcastsReturned?.map(
        (podcast) => ({
          title: podcast?.attributes?.title,
          slug: podcast?.attributes?.slug,
          excerpt: podcast?.attributes?.excerpt,
          link: `${podcast?.attributes?.path}/${podcast?.attributes?.slug}`,
          contentId: Number(podcast?.id),
          date: podcast?.attributes?.publishedAt,
          type: 'podcast',
          tempoDeDuracao: podcast?.attributes.duration,
          imageUrl:
            podcast?.attributes?.thumbnail?.data?.attributes?.url ||
            '/img/placeholder.png',
          imageTitle:
            podcast?.attributes?.thumbnail?.data?.attributes?.name ||
            podcast?.attributes?.thumbnail?.data?.attributes?.alternativeText,
          imageAltText:
            podcast?.attributes?.thumbnail?.data?.attributes?.alternativeText ||
            podcast?.attributes?.thumbnail?.data?.attributes?.name,
        })
      );

      const videosFormatted: IContent[] = allVideosReturned?.map((video) => ({
        title: video?.attributes?.title,
        slug: video?.attributes?.slug,
        excerpt: video?.attributes?.excerpt,
        link: `${video?.attributes?.path}/${video?.attributes?.slug}`,
        contentId: Number(video?.id),
        date: video?.attributes?.publishedAt,
        type: 'video',
        imageUrl:
          getVideoThumbFromYoutube(video?.attributes?.video_id) ||
          '/img/placeholder.png',
        imageTitle: video?.attributes?.title,
        imageAltText: video?.attributes?.title,
      }));

      const termsFormatted: IContent[] = allTermsReturned?.map((term) => ({
        title: term?.attributes?.title,
        slug: term?.attributes?.slug,
        excerpt: term?.attributes?.content,
        link: `${DEFAULT_ROUTES.terms}/${term?.attributes?.slug.slice(0, 1)}/${
          term?.attributes?.slug
        }`,
        contentId: Number(term?.id),
        type: 'term',
        date: term?.attributes?.publishedAt,
      }));

      return {
        posts: postsFormatted,
        podcasts: podcastsFormatted,
        videos: videosFormatted,
        terms: termsFormatted,
      };
    } catch (error) {
      this.catchIssueUseCase.handle({
        error,
        errorName: 'GetAllBookmarkContentDetailsFormattedUseCase',
      });

      return {
        posts: [],
        podcasts: [],
        videos: [],
        terms: [],
      };
    }
  }
}
